/**
 * useAICoachStore.js — Zustand store for AI Coach conversations.
 *
 * Persists conversations via storage adapter (localStorage + Firestore).
 * Shared between the floating widget and the full-page /ai-coach view.
 *
 * Phase 3: Supports actionable AI responses — messages can contain
 * proposed actions that users approve and execute inline.
 */

import { create } from 'zustand';
import { storage } from '../utils/storage';
import { chatWithCoach, buildDataContext } from '../utils/geminiService';
import { executeActions as runActions } from '../utils/actionExecutor';
import useAICreditStore from './useAICreditStore';
import useTaskStore from './useTaskStore';
import useProjectStore from './useProjectStore';
import useAreaStore from './useAreaStore';
import useResourceStore from './useResourceStore';
import useQuestStore from './useQuestStore';
import useFlowStore from './useFlowStore';
import useTimerStore from './useTimerStore';
import useGamificationStore from './useGamificationStore';
import useSessionStore from './useSessionStore';
import useUserStore from './useUserStore';

// ---------- Constants ----------

const STORAGE_KEY = 'ai_coach_conversations';
const MAX_CONVERSATIONS = 50;
const MAX_MESSAGES_PER_CONVERSATION = 100;
const HISTORY_WINDOW = 20; // Messages sent as context to Gemini
const TITLE_MAX_LENGTH = 40;

// ---------- Helpers ----------

function getStoredConversations() {
  return storage.get(STORAGE_KEY, []);
}

function saveConversations(conversations) {
  storage.set(STORAGE_KEY, conversations);
}

function autoTitle(firstMessage) {
  const trimmed = firstMessage.trim();
  if (trimmed.length <= TITLE_MAX_LENGTH) return trimmed;
  return trimmed.substring(0, TITLE_MAX_LENGTH) + '…';
}

const GATEABLE_MODULES = [
  'dashboard', 'projects', 'areas', 'calendar', 'quests',
  'vision', 'rituals', 'resources', 'reviews', 'archives',
];

function buildFullContext() {
  const isModuleEnabled = useUserStore.getState().isModuleEnabled;
  const enabledIds = GATEABLE_MODULES.filter(id => isModuleEnabled(id));
  const summaryLine = `Enabled modules: ${enabledIds.length ? enabledIds.join(', ') : '(none)'}`;

  // Core data is always sent. Module-gated domains pass empty arrays when off
  // so buildDataContext omits those sections.
  const dataContext = buildDataContext({
    tasks: useTaskStore.getState().tasks,
    projects: isModuleEnabled('projects') ? useProjectStore.getState().projects : [],
    areas: isModuleEnabled('areas') ? useAreaStore.getState().areas : [],
    resources: isModuleEnabled('resources') ? useResourceStore.getState().resources : [],
    // Quests drive prioritization — only leak them into coach context when the module
    // is on, and scope to the CURRENT quarter (the quarterly constraint engine) so
    // stale objectives from past quarters don't surface as current priorities.
    quests: isModuleEnabled('quests')
      ? (useQuestStore.getState().getCurrentQuests?.() ?? useQuestStore.getState().quests)
      : [],
    flows: useFlowStore.getState().flows,
    timerSettings: {
      focusDuration: useTimerStore.getState().focusDuration,
      restDuration: useTimerStore.getState().restDuration,
      reflectDuration: useTimerStore.getState().reflectDuration,
      prepDuration: useTimerStore.getState().prepDuration,
    },
  });

  const sessions = useSessionStore.getState().sessions || [];
  const recentSessions = sessions.slice(-7);
  const gamification = useGamificationStore.getState();

  const extraLines = [];

  // Sessions = core (always included)
  if (recentSessions.length > 0) {
    extraLines.push(`\nRECENT FOCUS SESSIONS (last ${recentSessions.length}):`);
    recentSessions.forEach(s => {
      const date = s.startTime ? s.startTime.split('T')[0] : (s.createdAt ? s.createdAt.split('T')[0] : 'unknown');
      const mins = Math.round((s.duration || 0) / 60);
      extraLines.push(`  - ${date}: ${mins} min focused`);
    });
  }

  // Gamification block gated behind the 'rituals' module (gamification is reached via rituals).
  if (isModuleEnabled('rituals') && gamification.profile) {
    const profile = gamification.profile;
    const level = gamification.getLevel?.() || { level: 1 };
    extraLines.push(`\nGAMIFICATION:`);
    extraLines.push(`  - Level: ${level.level} (${profile.totalXP || 0} XP)`);
    extraLines.push(`  - Current streak: ${profile.currentStreak || 0} days`);
    extraLines.push(`  - Total focus minutes: ${profile.totalFocusMinutes || 0}`);
  }

  const now = new Date();
  const timeLines = [];
  timeLines.push(`\nCURRENT CONTEXT:`);
  timeLines.push(`  - Date: ${now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`);
  timeLines.push(`  - Time: ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`);

  return summaryLine + '\n' + dataContext + '\n' + extraLines.join('\n') + '\n' + timeLines.join('\n');
}

// ---------- Response Parsing ----------

/**
 * Parse AI response text into { textContent, actions }.
 * The AI may embed actions between ---ACTIONS--- and ---END_ACTIONS--- markers.
 */
function parseCoachResponse(rawText) {
  const ACTIONS_START = '---ACTIONS---';
  const ACTIONS_END = '---END_ACTIONS---';

  const startIdx = rawText.indexOf(ACTIONS_START);
  const endIdx = rawText.indexOf(ACTIONS_END);

  // No action block found — return plain text
  if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) {
    return { textContent: rawText.trim(), actions: [] };
  }

  // Extract and clean text content (before and after action block)
  const beforeActions = rawText.substring(0, startIdx).trim();
  const afterActions = rawText.substring(endIdx + ACTIONS_END.length).trim();
  const textContent = [beforeActions, afterActions].filter(Boolean).join('\n\n');

  // Parse the JSON action block
  const actionJson = rawText.substring(startIdx + ACTIONS_START.length, endIdx).trim();
  let actions = [];

  try {
    const parsed = JSON.parse(actionJson);
    if (Array.isArray(parsed)) {
      actions = parsed.map((action, index) => ({
        ...action,
        id: `coach-action-${index}-${Date.now()}`,
        selected: true, // Default: all selected
      }));
    }
  } catch (e) {
    // If JSON parsing fails, treat the whole thing as text
    console.warn('Failed to parse AI Coach action block:', e);
    return { textContent: rawText.trim(), actions: [] };
  }

  return { textContent, actions };
}

/**
 * Build a human-readable label for an action.
 */
export function getActionLabel(action) {
  const d = action.data || {};
  // AI may use different field names — resolve flexibly
  const taskName = d.matchTitle || d.title || d.taskTitle || d.name || 'unknown';

  switch (action.type) {
    case 'add_subtasks': {
      const count = d.subtasks?.length || 0;
      const tName = d.taskTitle || d.matchTitle || d.title || 'task';
      return `Break "${tName}" into ${count} subtasks`;
    }
    case 'create_task':
      return `Create task: "${d.title || 'untitled'}"`;
    case 'complete_task':
      return `Mark "${taskName}" as done`;
    case 'prioritize_task':
      return `Set "${taskName}" to ${d.priority} priority`;
    case 'update_task':
      return `Update task "${taskName}"`;
    case 'delete_task':
      return `Delete task "${taskName}"`;
    case 'create_project':
      return `Create project: "${d.name}"`;
    case 'update_project':
      return `Update project "${d.matchName || d.name}"`;
    case 'delete_project':
      return `Archive project "${d.matchName || d.name}"`;
    case 'create_area':
      return `Create area: "${d.name}"`;
    case 'delete_area':
      return `Delete area "${d.matchName || d.name}"`;
    case 'create_resource':
      return `Create resource: "${d.title}"`;
    case 'delete_resource':
      return `Archive resource "${d.matchTitle || d.title}"`;
    default:
      return `${action.type}: ${JSON.stringify(d).substring(0, 50)}`;
  }
}

// ---------- Smart Suggested Prompts ----------

function getSmartPrompts(pageContext = '') {
  const hour = new Date().getHours();
  const prompts = [];

  if (hour < 12) {
    prompts.push("Help me plan my morning");
  } else if (hour < 17) {
    prompts.push("What should I focus on next?");
  } else {
    prompts.push("Help me review my day");
  }

  const ctx = pageContext.toLowerCase();
  if (ctx.includes('today')) {
    prompts.push("Help me prioritize today's tasks");
    prompts.push("Which task should I tackle first?");
  } else if (ctx.includes('focus')) {
    prompts.push("Break down my current task into smaller steps");
    prompts.push("How can I maintain focus during this session?");
  } else if (ctx.includes('stats')) {
    prompts.push("Analyze my focus patterns");
    prompts.push("How can I improve my focus time?");
  } else if (ctx.includes('project')) {
    prompts.push("Help me plan the next steps for this project");
    prompts.push("What's blocking progress on this project?");
  } else if (ctx.includes('all tasks')) {
    prompts.push("Which tasks should I prioritize this week?");
    prompts.push("Help me organize my task backlog");
  } else if (ctx.includes('weekly review')) {
    prompts.push("What went well this week?");
    prompts.push("Help me set goals for next week");
  } else {
    prompts.push("Break down my top-priority task");
    prompts.push("How is my week looking?");
  }

  prompts.push("I feel overwhelmed \u2014 help me prioritize");

  return prompts.slice(0, 5);
}

const DEFAULT_PROMPTS = getSmartPrompts();

// ---------- Store ----------

const useAICoachStore = create((set, get) => ({
  // State
  conversations: getStoredConversations(),
  activeConversationId: null,
  isWidgetOpen: false,
  widgetView: 'chatList', // 'chatList' | 'singleChat'
  isLoading: false,
  error: null,

  // Constants exposed for UI
  suggestedPrompts: DEFAULT_PROMPTS,
  getSmartPrompts: (pageContext) => getSmartPrompts(pageContext),

  // ---------- Widget Lifecycle ----------

  toggleWidget: () => {
    const { isWidgetOpen } = get();
    if (isWidgetOpen) {
      set({ isWidgetOpen: false, widgetView: 'chatList', activeConversationId: null });
    } else {
      set({ isWidgetOpen: true, widgetView: 'chatList' });
    }
  },

  openWidget: () => set({ isWidgetOpen: true, widgetView: 'chatList' }),
  closeWidget: () => set({ isWidgetOpen: false, widgetView: 'chatList', activeConversationId: null }),

  // ---------- Navigation ----------

  showChatList: () => set({ widgetView: 'chatList', activeConversationId: null }),

  openConversation: (convId) => {
    set({ activeConversationId: convId, widgetView: 'singleChat' });
  },

  // ---------- Conversation Management ----------

  createConversation: () => {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const welcomeMessage = {
      role: 'assistant',
      content: "Hey! I'm your AI coach. I can see your tasks, projects, focus patterns, and streaks. Ask me anything — from prioritizing your day to breaking down a big task into small steps. I can even make changes for you! ✨",
      timestamp: now,
    };
    const newConv = {
      id,
      title: 'New conversation',
      messages: [welcomeMessage],
      createdAt: now,
      updatedAt: now,
    };
    const { conversations } = get();
    const updated = [newConv, ...conversations].slice(0, MAX_CONVERSATIONS);
    saveConversations(updated);
    set({
      conversations: updated,
      activeConversationId: id,
      widgetView: 'singleChat',
      error: null,
    });
    return id;
  },

  deleteConversation: (convId) => {
    const { conversations, activeConversationId } = get();
    const updated = conversations.filter(c => c.id !== convId);
    saveConversations(updated);
    set({
      conversations: updated,
      activeConversationId: activeConversationId === convId ? null : activeConversationId,
      widgetView: activeConversationId === convId ? 'chatList' : get().widgetView,
    });
  },

  getActiveConversation: () => {
    const { conversations, activeConversationId } = get();
    return conversations.find(c => c.id === activeConversationId) || null;
  },

  // ---------- Messaging ----------

  sendMessage: async (text, { pageContext } = {}) => {
    if (!text.trim()) return;

    const { activeConversationId, conversations } = get();
    if (!activeConversationId) return;

    // Check AI availability (credits or BYOK key)
    const aiMode = useAICreditStore.getState().getAIMode('ai_coach');
    if (aiMode === 'none') {
      set({ error: 'NO_CREDITS_OR_KEY' });
      return;
    }

    const now = new Date().toISOString();
    const userMessage = { role: 'user', content: text.trim(), timestamp: now };

    const conv = conversations.find(c => c.id === activeConversationId);
    if (!conv) return;

    const updatedMessages = [...conv.messages, userMessage];

    const isFirstUserMessage = !conv.messages.some(m => m.role === 'user');
    const title = isFirstUserMessage ? autoTitle(text.trim()) : conv.title;

    const updatedConv = { ...conv, messages: updatedMessages, title, updatedAt: now };
    const updatedConversations = conversations.map(c =>
      c.id === activeConversationId ? updatedConv : c
    );
    saveConversations(updatedConversations);
    set({ conversations: updatedConversations, isLoading: true, error: null });

    try {
      let dataContext = buildFullContext();
      if (pageContext) {
        dataContext += `\n\nUSER IS CURRENTLY ON: ${pageContext}`;
      }
      const historyMessages = updatedMessages
        .filter(m => m.role === 'user' || (m.role === 'assistant' && m.content))
        .slice(-HISTORY_WINDOW)
        .map(m => ({ role: m.role, content: m.content }));

      const response = await chatWithCoach({
        message: text.trim(),
        conversationHistory: historyMessages.slice(0, -1),
        dataContext,
        mode: aiMode,
      });

      // Parse response for actions
      const { textContent, actions } = parseCoachResponse(response.content);

      const aiMessage = {
        role: 'assistant',
        content: textContent,
        timestamp: new Date().toISOString(),
        actionsApplied: false,
      };
      if (actions.length > 0) {
        aiMessage.actions = actions;
      }

      const { conversations: currentConvs } = get();
      const currentConv = currentConvs.find(c => c.id === activeConversationId);
      if (currentConv) {
        const finalMessages = [...currentConv.messages, aiMessage];
        const finalConv = { ...currentConv, messages: finalMessages, updatedAt: aiMessage.timestamp };
        const finalConversations = currentConvs.map(c =>
          c.id === activeConversationId ? finalConv : c
        );
        saveConversations(finalConversations);
        set({ conversations: finalConversations, isLoading: false });
      }
    } catch (error) {
      const errMsg = error.message || 'Failed to get response';
      // If credits ran out mid-request, surface specific error
      if (errMsg === 'INSUFFICIENT_CREDITS') {
        set({ isLoading: false, error: 'INSUFFICIENT_CREDITS' });
      } else {
        set({ isLoading: false, error: errMsg });
      }
    }
  },

  clearError: () => set({ error: null }),

  // ---------- Action Management (Phase 3) ----------

  /**
   * Toggle an action's `selected` state within a message.
   * @param {string} convId - Conversation ID
   * @param {number} msgIndex - Index of the message in conversation
   * @param {string} actionId - The action's ID
   */
  toggleAction: (convId, msgIndex, actionId) => {
    const { conversations } = get();
    const updated = conversations.map(conv => {
      if (conv.id !== convId) return conv;
      const messages = conv.messages.map((msg, idx) => {
        if (idx !== msgIndex || !msg.actions) return msg;
        return {
          ...msg,
          actions: msg.actions.map(a =>
            a.id === actionId ? { ...a, selected: !a.selected } : a
          ),
        };
      });
      return { ...conv, messages };
    });
    saveConversations(updated);
    set({ conversations: updated });
  },

  /**
   * Execute the selected actions from a message.
   * @param {string} convId - Conversation ID
   * @param {number} msgIndex - Index of the message with actions
   */
  applyActions: async (convId, msgIndex) => {
    const { conversations } = get();
    const conv = conversations.find(c => c.id === convId);
    if (!conv) return;

    const msg = conv.messages[msgIndex];
    if (!msg?.actions || msg.actionsApplied) return;

    const selectedActions = msg.actions.filter(a => a.selected);
    if (selectedActions.length === 0) return;

    // Execute actions
    const results = await runActions(selectedActions);

    // Build confirmation message
    const successCount = results.succeeded.length;
    const failCount = results.failed.length;
    let confirmContent = `✅ ${successCount} action${successCount !== 1 ? 's' : ''} applied!`;
    if (failCount > 0) {
      confirmContent += ` ⚠️ ${failCount} failed.`;
      results.failed.forEach(f => {
        confirmContent += `\n• ${f.error}`;
      });
    }

    // Note: AI-Coach actions are entity CRUD (create/update/delete
    // task/project/area/resource/identity/flow) — they earn no focus XP.

    const confirmMessage = {
      role: 'assistant',
      content: confirmContent,
      timestamp: new Date().toISOString(),
      isConfirmation: true,
    };

    // Mark actions as applied and append confirmation
    const updatedConversations = conversations.map(c => {
      if (c.id !== convId) return c;
      const messages = c.messages.map((m, idx) => {
        if (idx !== msgIndex) return m;
        return { ...m, actionsApplied: true };
      });
      messages.push(confirmMessage);
      return { ...c, messages, updatedAt: confirmMessage.timestamp };
    });

    saveConversations(updatedConversations);
    set({ conversations: updatedConversations });
  },

  // ---------- Contextual Actions ----------

  openWidgetWithMessage: async (text, { pageContext } = {}) => {
    const id = get().createConversation();
    set({ isWidgetOpen: true, widgetView: 'singleChat', activeConversationId: id });
    await get().sendMessage(text, { pageContext });
  },

  sendMessageWithoutOpening: async (text, { pageContext } = {}) => {
    const id = get().createConversation();
    // Do NOT set isWidgetOpen to true
    set({ activeConversationId: id, widgetView: 'singleChat' });
    await get().sendMessage(text, { pageContext });
  },
}));

export default useAICoachStore;
